import { cn } from '@/lib/cn';

/**
 * Panel — la tarjeta de un bloque, con su título, su vacío y su fallo.
 *
 * Nació en Inicio el 2026-09-07 y subió aquí el 2026-09-08, al migrar
 * Novedades: las dos pantallas montan los mismos tres paneles laterales
 * —reconocimientos, celebraciones, eventos— y dejarlas con una copia cada una
 * garantizaba que divergieran a la primera corrección. Es el mismo motivo por
 * el que la tarjeta de publicación vive en un solo sitio.
 *
 * Plana, con filete de 1 px y sin sombra — la decisión del 2026-09-01, medida:
 * había 21 sombras en reposo sobre un fondo que ya era gris, y con doce tarjetas
 * a la vez ninguna destacaba.
 *
 * El título va a `--fs-3`/600 en navy y lo que lleva debajo a `--fs-2` en gris:
 * dos señales —tamaño y color— en vez de colgar la jerarquía solo del grosor de
 * la letra, que a 12 px casi no se ve.
 */
export function Panel({
  icono, titulo, children, className, pie,
}: {
  /** Clase de Bootstrap Icons, p. ej. `bi-trophy-fill`. */
  icono: string;
  titulo: string;
  children: React.ReactNode;
  className?: string;
  /** Enlace de cierre, del tipo «Ver todos». */
  pie?: React.ReactNode;
}) {
  return (
    <section className={cn('rounded-md border border-line bg-surface p-4', className)}>
      <h2 className="mb-3 flex items-center gap-2 text-3 font-semibold text-brand-ink">
        {/* Decorativo: la palabra de al lado ya dice lo mismo, y repetirlo solo
            alarga la lectura del lector de pantalla. */}
        <i className={cn('bi', icono)} aria-hidden="true" />
        {titulo}
      </h2>
      {children}
      {pie}
    </section>
  );
}

/** El «Ver todos» del pie de un panel. Secundario a propósito: es un destino,
 *  no la acción de la pantalla. */
export function PieDePanel({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="mt-3 flex items-center justify-center gap-1 border-t border-line pt-2
                 text-2 font-semibold text-muted transition-colors hover:text-brand-ink
                 focus-visible:focus-anillo
                 pointer-coarse:min-h-11"
    >
      {children} <i className="bi bi-arrow-right" aria-hidden="true" />
    </a>
  );
}

/**
 * Lo que se enseña cuando el bloque no cargó.
 *
 * Existe porque la versión vanilla tenía **cinco `catch` vacíos**: si
 * `/solicitudes/resumen` devolvía 500, la franja de pendientes no aparecía y
 * nadie se enteraba — desde fuera es idéntico a «no tienes nada pendiente», que
 * es justo la conclusión equivocada.
 *
 * Distingue por código igual que `EstadoError`, y por el mismo motivo: en un 403
 * reintentar no arregla nada, así que no se ofrece. Aquí se queda en dos líneas
 * porque vive dentro de una tarjeta de 320 px; el bloque grande es para cuando
 * falla la pantalla entera.
 */
export function FalloDePanel({
  que, err, onReintentar,
}: {
  /** Qué no cargó, en palabras del usuario: «los reconocimientos». */
  que: string;
  err: unknown;
  onReintentar?: () => void;
}) {
  const codigo = (err as { status?: number })?.status;
  const sinPermiso = codigo === 403;
  return (
    <div className="flex flex-col items-start gap-2 py-1">
      <p className="text-2 text-muted" role="alert">
        <i
          className={cn('bi', sinPermiso ? 'bi-shield-lock' : 'bi-exclamation-triangle', 'mr-1')}
          aria-hidden="true"
        />
        {sinPermiso ? `${que}: esto no está en tu rol.` : `No pudimos cargar ${que}.`}
      </p>
      {!sinPermiso && onReintentar && (
        <button
          type="button"
          onClick={onReintentar}
          className="text-2 font-semibold text-brand-ink underline underline-offset-2
                     focus-visible:focus-anillo"
        >
          Reintentar
        </button>
      )}
    </div>
  );
}

/** El vacío de un panel: una línea, sin caja. El bloque grande de
 *  `EstadoVacio` no cabe en una tarjeta de 320 px. */
export function VacioDePanel({ children }: { children: React.ReactNode }) {
  return <p className="py-1 text-center text-3 text-muted">{children}</p>;
}
